import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Clock, X } from 'lucide-react'
import { isTimeOverrideEnabled } from '@/lib/timeOverride'
import { TimeOverridePanel } from './TimeOverridePanel'

const isMac =
  typeof navigator !== 'undefined' && /Mac|iPhone|iPad/i.test(navigator.userAgent)
const SHORTCUT_LABEL = isMac ? '⌃⇧K' : 'Ctrl+Shift+K'

/**
 * Global hotkey + modal entry point for the time-override panel.
 * Mounts once at app root. Ctrl+Shift+K (Cmd+Shift+K on Mac) toggles
 * the dialog from anywhere in the app.
 *
 * Does NOT render the topbar badge — that's TimeOverrideBadge.
 */
export const TimeOverrideLauncher = () => {
  const [open, setOpen] = useState(false)
  const enabled = isTimeOverrideEnabled()

  useEffect(() => {
    if (!enabled) return
    const handler = (e: KeyboardEvent) => {
      const mod = isMac ? e.metaKey : e.ctrlKey
      if (mod && e.shiftKey && (e.key === 'K' || e.key === 'k')) {
        e.preventDefault()
        e.stopPropagation()
        setOpen((prev) => !prev)
      }
    }
    window.addEventListener('keydown', handler, true)
    return () => window.removeEventListener('keydown', handler, true)
  }, [enabled])

  if (!enabled || !open) return null

  return createPortal(
    <div className="fixed inset-0 z-1000 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-150">
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl animate-in zoom-in-95 duration-150">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <Clock size={18} className="text-[#3C3295]" />
            <h2 className="text-base font-bold text-[#05004E]">Time Override</h2>
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800">
              QA tool
            </span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="cursor-pointer rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        <TimeOverridePanel variant="modal" />

        <div className="border-t border-slate-100 bg-slate-50 px-6 py-3 text-[11px] text-slate-500 flex items-center justify-between">
          <span>
            Shortcut: <kbd className="rounded bg-white px-1.5 py-0.5 font-mono text-[10px] font-bold text-[#3C3295] shadow-sm">{SHORTCUT_LABEL}</kbd>
          </span>
          <a
            href="/portal/dev/time"
            onClick={() => setOpen(false)}
            className="font-bold text-[#3C3295] hover:text-[#FF017E] hover:underline"
          >
            Open full page →
          </a>
        </div>
      </div>
    </div>,
    document.body
  )
}
